import type { ExportFormat } from "@cfg/kernel";
import type { ParamSpec } from "@cfg/design-sdk";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useConfiguratorContext, useConfiguratorStore } from "./context.tsx";
import { isDirty, isSettled, waitForSettled } from "./store.ts";
import { createDelayedFlag, type DelayedFlagOptions } from "./timing.ts";

/** The loaded design's description (id, version, meta, ui, spec). */
export function useDesign() {
  const description = useConfiguratorStore((s) => s.description);
  const status = useConfiguratorStore((s) => s.status);
  const error = useConfiguratorStore((s) => s.error);
  const spec = useMemo(() => (description?.spec ?? []) as ParamSpec[], [description]);
  return { description, spec, status, error, loaded: description !== null };
}

/** Live parameters and setters. */
export function useParams() {
  const params = useConfiguratorStore((s) => s.params);
  const dirty = useConfiguratorStore((s) => isDirty(s));
  const { store } = useConfiguratorContext();
  const setParam = useCallback((name: string, value: unknown) => store.getState().setParams({ [name]: value }), [store]);
  const setParams = useCallback((patch: Record<string, unknown>) => store.getState().setParams(patch), [store]);
  const reset = useCallback(() => store.getState().reset(), [store]);
  const applyPreset = useCallback((id: string) => store.getState().applyPreset(id), [store]);
  const setActive = useCallback((name: string | null) => store.getState().setActiveParam(name), [store]);
  return { params, setParam, setParams, reset, applyPreset, dirty, setActive };
}

/** Current preview mesh, stats and print-check issues. */
export function useMesh() {
  const mesh = useConfiguratorStore((s) => s.mesh);
  const stats = useConfiguratorStore((s) => s.stats);
  const issues = useConfiguratorStore((s) => s.issues);
  const status = useConfiguratorStore((s) => s.status);
  const buildCount = useConfiguratorStore((s) => s.buildCount);
  return { mesh, stats, issues, building: status === "building" || status === "loading", status, buildCount };
}

export interface ExportState {
  /** Format being produced; set from the click until the file is handed over. */
  busy: ExportFormat | null;
  /** "preparing": waiting for an in-flight preview rebuild (so print-check issues are current); "exporting": on the worker. */
  phase: "preparing" | "exporting" | null;
  error: string | null;
  /** The click waited for a rebuild and the result has blocking print-check errors; nothing was exported. */
  blocked: boolean;
  last: { filename: string; bytes: number; format: ExportFormat } | null;
}

/**
 * Export at print quality on the heavy worker and hand the file to the browser.
 * A click while the preview is rebuilding waits for that build (phase "preparing"), then
 * refuses if it reports a print-check error, so buttons never need to flip disabled mid-drag.
 */
export function useExport(onExported?: (info: { format: ExportFormat; filename: string; bytes: number }) => void) {
  const { store, client } = useConfiguratorContext();
  const [state, setState] = useState<ExportState>({ busy: null, phase: null, error: null, blocked: false, last: null });
  const onExportedRef = useRef(onExported);
  onExportedRef.current = onExported;

  const exportAs = useCallback(
    async (format: ExportFormat): Promise<boolean> => {
      let s = store.getState();
      if (!s.key) return false;
      const waiting = !isSettled(s);
      setState((st) => ({ ...st, busy: format, phase: waiting ? "preparing" : "exporting", error: null, blocked: false }));
      try {
        if (waiting) s = await waitForSettled(store);
        if (s.issues.some((i) => i.level === "error")) {
          setState((st) => ({ ...st, busy: null, phase: null, blocked: true }));
          return false;
        }
        setState((st) => ({ ...st, phase: "exporting" }));
        const result = await client.export(s.key!, s.params, format);
        downloadBlob(result.data, result.filename, format === "stl" ? "model/stl" : "model/3mf");
        const info = { format, filename: result.filename, bytes: result.data.byteLength };
        setState({ busy: null, phase: null, error: null, blocked: false, last: info });
        onExportedRef.current?.(info);
        return true;
      } catch (err) {
        setState((st) => ({ ...st, busy: null, phase: null, error: (err as Error).message }));
        return false;
      }
    },
    [client, store],
  );

  return { ...state, exportStl: () => exportAs("stl"), export3mf: () => exportAs("3mf"), exportAs };
}

/**
 * Busy state for display. `building` is the raw worker state (true for every short rebuild);
 * `showBusy` turns on only after `delayMs` (250) of continuous building and then stays on at
 * least `minVisibleMs` (400), so slider drags don't strobe the UI. `pending` is true while the
 * preview does not match the current params yet (debounce window or rebuild in flight).
 */
export function useBusy(opts?: DelayedFlagOptions) {
  const status = useConfiguratorStore((s) => s.status);
  const pending = useConfiguratorStore((s) => s.description !== null && !isSettled(s));
  const building = status === "building" || status === "loading";
  const showBusy = useDelayedFlag(building, opts);
  return { building, pending, showBusy };
}

/** Delays a boolean for display (see `createDelayedFlag`): on after `delayMs` of continuous true, then at least `minVisibleMs`. */
export function useDelayedFlag(flag: boolean, opts?: DelayedFlagOptions): boolean {
  const [on, setOn] = useState(false);
  const gate = useRef<ReturnType<typeof createDelayedFlag> | null>(null);
  if (!gate.current) gate.current = createDelayedFlag(setOn, opts);
  useEffect(() => gate.current!.set(flag), [flag]);
  useEffect(() => () => gate.current?.dispose(), []);
  return on;
}

/** True for `ms` after `value` changes (not on first render). For `data-changed` on stat chips. */
export function useChanged(value: unknown, ms = 600): boolean {
  const [changed, setChanged] = useState(false);
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    setChanged(true);
    const t = setTimeout(() => setChanged(false), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return changed;
}

/**
 * Transient "done" feedback: `flash(id)` sets `done` to `id` for `ms` (default 1.6 s) so a button
 * can read "✓ Saved" and carry `.is-done`. A new flash replaces the previous one.
 */
export function useFlash(ms = 1600) {
  const [done, setDone] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const flash = useCallback(
    (id: string) => {
      if (timer.current) clearTimeout(timer.current);
      setDone(id);
      timer.current = setTimeout(() => setDone(null), ms);
    },
    [ms],
  );
  useEffect(() => () => clearTimeout(timer.current), []);
  return { done, flash };
}

/**
 * Reveal a param's field: opens its group, scrolls it into view (smooth unless reduced
 * motion) and focuses its input. Works through `<ParamForm>`; harmless without one.
 */
export function useFocusParam() {
  const { store } = useConfiguratorContext();
  return useCallback((name: string) => store.getState().requestFocus(name), [store]);
}

export function downloadBlob(data: ArrayBuffer | Uint8Array, filename: string, type: string): void {
  if (typeof document === "undefined") return;
  const blob = new Blob([data as BlobPart], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Migrate params saved under an older version to the loaded design's version. */
export function useMigrate() {
  const { client, store } = useConfiguratorContext();
  return useCallback(
    async (fromVersion: string, params: Record<string, unknown>) => {
      const s = store.getState();
      if (!s.key) throw new Error("No design loaded");
      const migrated = await client.migrate(s.key, fromVersion, params);
      s.replaceParams(migrated);
      return migrated;
    },
    [client, store],
  );
}
